import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    ArrowRightIcon,
    BuildingOffice2Icon,
    MapPinIcon,
} from "@heroicons/react/24/outline";
import Hero from "../components/Hero";
import destImg from "../assests/images/hotel-bg.jpg";

export default function DestinationsList({
    showBanner = true,
    showHeading = true,
}) {
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);

    const navigate = useNavigate();

    // Fetch destinations
    const fetchDestinations = async () => {
        setLoading(true);

        try {
            const res = await axios.get(
                "https://dubaihotelrates.com/api/destinations"
            );

            setDestinations(res.data);
        } catch (err) {
            console.error("Failed to fetch destinations:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDestinations();
    }, []);

    return (
        <div className="w-full bg-white">

            {/* =========================================================
                HERO
            ========================================================= */}
            {showBanner && (
                <Hero
                    variant="inner"
                    title="Explore Dubai Destinations"
                    subtitle="Discover exceptional stays and unforgettable experiences across Dubai"
                    backgroundImage={destImg}
                />
            )}

            {/* =========================================================
                MAIN CONTENT
            ========================================================= */}
            <section className="relative overflow-hidden bg-[#F7FAFF] py-14 sm:py-16 lg:py-20">

                {/* Decorative background elements */}
                <div className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full bg-[#174EA6]/5 blur-3xl" />
                <div className="pointer-events-none absolute -right-32 bottom-10 h-80 w-80 rounded-full bg-[#FFC928]/10 blur-3xl" />

                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

                    {/* =====================================================
                        HEADING
                    ===================================================== */}
                    {showHeading && (
                        <div className="mb-10 sm:mb-12">

                            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">

                                <div className="max-w-2xl">

                                    {/* Eyebrow */}
                                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#174EA6]/10 bg-white px-4 py-2 shadow-sm">
                                        <span className="h-2 w-2 rounded-full bg-[#FFC928]" />

                                        <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#174EA6]">
                                            Discover Dubai
                                        </span>
                                    </div>

                                    <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-[#0F172A] sm:text-4xl lg:text-5xl">
                                        Find Your Perfect
                                        <span className="block text-[#174EA6]">
                                            Dubai Destination
                                        </span>
                                    </h2>

                                    <p className="mt-4 max-w-xl text-sm leading-7 text-slate-600 sm:text-base">
                                        Explore Dubai's most popular areas and
                                        discover hotels that match the way you
                                        want to travel.
                                    </p>
                                </div>

                                {/* Destination count */}
                                {!loading && destinations.length > 0 && (
                                    <div className="hidden shrink-0 lg:block">
                                        <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">

                                            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF3FF]">
                                                <MapPinIcon className="h-6 w-6 text-[#174EA6]" />
                                            </div>

                                            <div>
                                                <p className="text-2xl font-extrabold leading-none text-[#0F172A]">
                                                    {destinations.length}
                                                </p>

                                                <p className="mt-1 text-xs font-medium text-slate-500">
                                                    Destinations
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Gold divider */}
                            <div className="mt-8 h-1 w-16 rounded-full bg-[#FFC928]" />
                        </div>
                    )}

                    {/* =====================================================
                        LOADING SKELETON
                    ===================================================== */}
                    {loading ? (
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-12">

                            {/* Large skeleton */}
                            <div className="h-[390px] animate-pulse overflow-hidden rounded-[28px] bg-slate-200 sm:col-span-2 lg:col-span-7" />

                            {/* Small skeletons */}
                            <div className="h-[185px] animate-pulse rounded-[28px] bg-slate-200 lg:col-span-5" />

                            <div className="h-[185px] animate-pulse rounded-[28px] bg-slate-200 lg:col-span-5" />

                            <div className="h-[185px] animate-pulse rounded-[28px] bg-slate-200 lg:col-span-4" />

                            <div className="h-[185px] animate-pulse rounded-[28px] bg-slate-200 lg:col-span-4" />

                            <div className="h-[185px] animate-pulse rounded-[28px] bg-slate-200 lg:col-span-4" />
                        </div>
                    ) : destinations.length === 0 ? (

                        /* =================================================
                           EMPTY STATE
                        ================================================= */
                        <div className="rounded-[28px] border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">

                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#EAF3FF]">
                                <MapPinIcon className="h-8 w-8 text-[#174EA6]" />
                            </div>

                            <h3 className="mt-5 text-xl font-bold text-[#0F172A]">
                                No destinations available
                            </h3>

                            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
                                We couldn't find any destinations right now.
                                Please check again shortly.
                            </p>
                        </div>

                    ) : (

                        /* =================================================
                           DESTINATION GRID
                        ================================================= */
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-12">

                            {destinations.map((dest, index) => {

                                /*
                                 * First destination gets the large featured card.
                                 * Remaining cards use the smaller layout.
                                 */
                                const isFeatured = index === 0;

                                return (
                                    <div
                                        key={dest.id}
                                        onClick={() =>
                                            navigate(
                                                `/destinations/${dest.slug}`
                                            )
                                        }
                                        className={`
                                            group relative cursor-pointer overflow-hidden
                                            rounded-[28px] bg-slate-200
                                            shadow-sm
                                            transition-all duration-500
                                            hover:-translate-y-1
                                            hover:shadow-2xl
                                            ${isFeatured
                                                ? "h-[390px] sm:col-span-2 lg:col-span-7"
                                                : "h-[250px] lg:col-span-5"
                                            }
                                        `}
                                    >

                                        {/* =================================================
                                            IMAGE
                                        ================================================= */}
                                        <img
                                            src={`https://uaediscountedhotels.com/uploads/destinations/${dest.image}`}
                                            alt={dest.name}
                                            loading={index > 2 ? "lazy" : "eager"}
                                            className="
                                                absolute inset-0
                                                h-full w-full
                                                object-cover
                                                transition-transform duration-700
                                                ease-out
                                                group-hover:scale-110
                                            "
                                        />

                                        {/* =================================================
                                            IMAGE OVERLAY
                                        ================================================= */}
                                        <div className="
                                            absolute inset-0
                                            bg-gradient-to-t
                                            from-[#001A2C]/95
                                            via-[#002B45]/35
                                            to-transparent
                                            transition-all duration-500
                                            group-hover:from-[#001A2C]
                                            group-hover:via-[#002B45]/45
                                        " />

                                        {/* Subtle blue hover glow */}
                                        <div className="
                                            absolute inset-0
                                            bg-gradient-to-br
                                            from-[#174EA6]/0
                                            via-transparent
                                            to-[#174EA6]/30
                                            opacity-0
                                            transition-opacity duration-500
                                            group-hover:opacity-100
                                        " />

                                        {/* =================================================
                                            TOP LEFT NUMBER
                                        ================================================= */}
                                        <div className="absolute left-4 top-4 sm:left-5 sm:top-5">

                                            <div className="
                                                flex h-9 min-w-9 items-center
                                                justify-center rounded-full
                                                border border-white/20
                                                bg-[#002B45]/70
                                                px-2
                                                text-xs font-bold text-white
                                                shadow-lg
                                                backdrop-blur-md
                                            ">
                                                {String(index + 1).padStart(2, "0")}
                                            </div>
                                        </div>

                                        {/* =================================================
                                            HOTEL COUNT
                                        ================================================= */}
                                        <div className="absolute right-4 top-4 sm:right-5 sm:top-5">

                                            <div className="
                                                flex h-8 items-center gap-1.5
                                                rounded-full
                                                border border-white/20
                                                bg-white/15
                                                px-3
                                                shadow-lg
                                                backdrop-blur-md
                                            ">
                                                <BuildingOffice2Icon className="h-3.5 w-3.5 text-[#FFC928]" />

                                                <span className="text-[10px] font-bold text-white sm:text-xs">
                                                    {dest.hotels_count || 0} Hotels
                                                </span>
                                            </div>
                                        </div>

                                        {/* =================================================
                                            CONTENT
                                        ================================================= */}
                                        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">

                                            <div className="flex items-end justify-between gap-4">

                                                <div className="min-w-0">

                                                    <div className="mb-2 flex items-center gap-2">

                                                        <span className="
                                                            h-1.5 w-7 rounded-full
                                                            bg-[#FFC928]
                                                        " />

                                                        <span className="
                                                            text-[10px] font-bold
                                                            uppercase tracking-[0.18em]
                                                            text-[#FFC928]
                                                        ">
                                                            Dubai
                                                        </span>
                                                    </div>

                                                    <h3
                                                        className={`
                                                            truncate font-extrabold
                                                            tracking-tight text-white
                                                            ${isFeatured
                                                                ? "text-2xl sm:text-3xl lg:text-4xl"
                                                                : "text-xl sm:text-2xl"
                                                            }
                                                        `}
                                                    >
                                                        {dest.name}
                                                    </h3>

                                                    <p className="
                                                        mt-1.5 text-xs
                                                        text-white/70
                                                        sm:text-sm
                                                    ">
                                                        Explore hotels and stays
                                                    </p>
                                                </div>

                                                {/* Arrow */}
                                                <div className="
                                                    flex h-11 w-11 shrink-0
                                                    items-center justify-center
                                                    rounded-full
                                                    bg-white
                                                    text-[#174EA6]
                                                    shadow-xl
                                                    transition-all duration-300
                                                    group-hover:bg-[#FFC928]
                                                    group-hover:text-[#002B45]
                                                    group-hover:rotate-[-45deg]
                                                ">
                                                    <ArrowRightIcon className="h-5 w-5" />
                                                </div>
                                            </div>

                                            {/* Bottom accent */}
                                            <div className="
                                                mt-5 h-[2px] w-full
                                                overflow-hidden rounded-full
                                                bg-white/15
                                            ">
                                                <div className="
                                                    h-full w-0 rounded-full
                                                    bg-[#FFC928]
                                                    transition-all duration-500
                                                    group-hover:w-full
                                                " />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* =====================================================
                        BOTTOM CTA
                    ===================================================== */}
                    {!loading && destinations.length > 0 && (
                        <div className="
                            mt-10 overflow-hidden rounded-[28px]
                            border border-[#174EA6]/10
                            bg-white
                            shadow-sm
                        ">
                            <div className="
                                flex flex-col gap-5
                                px-6 py-6
                                sm:px-8 sm:py-7
                                md:flex-row md:items-center
                                md:justify-between
                            ">

                                <div className="flex items-center gap-4">

                                    <div className="
                                        hidden h-12 w-12 shrink-0
                                        items-center justify-center
                                        rounded-2xl bg-[#EAF3FF]
                                        sm:flex
                                    ">
                                        <MapPinIcon className="h-6 w-6 text-[#174EA6]" />
                                    </div>

                                    <div>
                                        <h3 className="text-base font-bold text-[#0F172A] sm:text-lg">
                                            Ready to explore Dubai?
                                        </h3>

                                        <p className="mt-1 text-xs text-slate-500 sm:text-sm">
                                            Find the perfect hotel for your next stay.
                                        </p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => navigate("/hotels")}
                                    className="
                                        inline-flex items-center
                                        justify-center gap-2
                                        rounded-xl
                                        bg-gradient-to-r
                                        from-[#174EA6]
                                        to-[#002B45]
                                        px-5 py-3
                                        text-sm font-bold text-white
                                        shadow-md
                                        transition-all duration-300
                                        hover:-translate-y-0.5
                                        hover:shadow-lg
                                    "
                                >
                                    Browse Hotels

                                    <ArrowRightIcon className="h-4 w-4" />
                                </button>
                            </div>

                            {/* Bottom gold line */}
                            <div className="
                                h-1 w-full
                                bg-gradient-to-r
                                from-[#174EA6]
                                via-[#FFC928]
                                to-[#174EA6]
                            " />
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}